import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  DocumentChartBarIcon,
  PlusIcon,
  TrashIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { craApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';

const DOCUMENT_TYPES = [
  { value: 'CRA_NOTICE', label: 'CRA Notice' },
  { value: 'ASSESSMENT_AREA_MAP', label: 'Assessment Area Map' },
  { value: 'PUBLIC_COMMENT', label: 'Public Comment' },
  { value: 'PERFORMANCE_EVALUATION', label: 'Performance Evaluation' },
];

const REQUIRED_DOCUMENTS = [
  { type: 'CRA_NOTICE', label: 'CRA Public Notice', description: 'Required notice per 12 CFR 228.44 informing the public of their right to comment' },
  { type: 'ASSESSMENT_AREA_MAP', label: 'Assessment Area Map', description: 'Map showing the delineation of assessment areas' },
  { type: 'PERFORMANCE_EVALUATION', label: 'Performance Evaluation', description: 'Most recent CRA performance evaluation from the supervisory agency' },
];

export default function CraPublicFilePage() {
  const { teamId, isLoading: teamLoading } = useTeamId();
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['cra-public-files', teamId],
    queryFn: async () => {
      const res = await craApi.listPublicFiles(teamId!);
      return res.data;
    },
    enabled: !!teamId,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => craApi.deletePublicFile(teamId!, id),
    onSuccess: () => {
      toast.success('Document deleted');
      queryClient.invalidateQueries({ queryKey: ['cra-public-files'] });
    },
    onError: () => toast.error('Failed to delete'),
  });

  const generateNoticeMutation = useMutation({
    mutationFn: () => craApi.generateNotice(teamId!),
    onSuccess: () => {
      toast.success('CRA Notice generated');
      queryClient.invalidateQueries({ queryKey: ['cra-public-files'] });
    },
    onError: () => toast.error('Failed to generate notice'),
  });

  if (teamLoading || isLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 w-64 bg-gray-200 rounded" />
        <div className="h-96 bg-gray-200 rounded-lg" />
      </div>
    );
  }

  const files = data?.publicFiles || [];
  const existingTypes = new Set(files.map((f: any) => f.documentType));

  return (
    <div>
      <div className="mb-6">
        <Link to="/cra" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3">
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back to CRA Dashboard
        </Link>
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-navy-900">CRA Public File</h1>
            <p className="mt-1 text-sm text-gray-500">Manage required CRA public file documents per 12 CFR 228.43</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => generateNoticeMutation.mutate()}
              disabled={generateNoticeMutation.isPending}
              className="inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            >
              <DocumentTextIcon className="h-4 w-4" />
              {generateNoticeMutation.isPending ? 'Generating...' : 'Auto-Generate Notice'}
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium bg-ocean-600 text-white hover:bg-ocean-700"
            >
              <PlusIcon className="h-4 w-4" />
              Add Document
            </button>
          </div>
        </div>
      </div>

      {/* Required Documents Checklist */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Required Documents Checklist</h2>
        </div>
        <div className="divide-y divide-gray-100">
          {REQUIRED_DOCUMENTS.map(doc => {
            const hasDoc = existingTypes.has(doc.type);
            return (
              <div key={doc.type} className="px-6 py-4 flex items-center gap-4">
                {hasDoc ? (
                  <CheckCircleIcon className="h-5 w-5 text-green-500 shrink-0" />
                ) : (
                  <ClockIcon className="h-5 w-5 text-gray-300 shrink-0" />
                )}
                <div className="flex-1">
                  <p className={`text-sm font-medium ${hasDoc ? 'text-gray-900' : 'text-gray-500'}`}>{doc.label}</p>
                  <p className="text-xs text-gray-500">{doc.description}</p>
                </div>
                {hasDoc ? (
                  <span className="text-xs font-medium text-green-600">On file</span>
                ) : (
                  <span className="text-xs font-medium text-amber-600">Missing</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Documents List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">All Documents ({files.length})</h2>
        </div>
        {files.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {files.map((file: any) => (
              <div key={file.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <DocumentChartBarIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.title}</p>
                    <p className="text-xs text-gray-500">
                      {DOCUMENT_TYPES.find(t => t.value === file.documentType)?.label || file.documentType}
                      {' · '}
                      {file.year}
                      {file.isPublished && ' · Published'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {file.isPublished ? (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">Published</span>
                  ) : (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">Draft</span>
                  )}
                  <button
                    onClick={() => {
                      if (confirm('Delete this document?')) deleteMutation.mutate(file.id);
                    }}
                    className="p-1.5 text-gray-400 hover:text-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-6 py-16 text-center">
            <DocumentChartBarIcon className="mx-auto h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-base font-semibold text-navy-900">No Documents</h3>
            <p className="mt-1 text-sm text-gray-500 max-w-sm mx-auto">
              Start by auto-generating your CRA notice or uploading required public file documents.
            </p>
          </div>
        )}
      </div>

      {modalOpen && (
        <PublicFileModal teamId={teamId!} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
}

function PublicFileModal({ teamId, onClose }: { teamId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    title: '',
    documentType: 'CRA_NOTICE',
    year: new Date().getFullYear(),
    description: '',
  });

  const mutation = useMutation({
    mutationFn: (data: any) => craApi.createPublicFile(teamId, data),
    onSuccess: () => {
      toast.success('Document created');
      queryClient.invalidateQueries({ queryKey: ['cra-public-files'] });
      onClose();
    },
    onError: () => toast.error('Failed to create document'),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add Document</h3>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Document Type</label>
            <select
              value={formData.documentType}
              onChange={e => setFormData(prev => ({ ...prev, documentType: e.target.value }))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            >
              {DOCUMENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <input
              type="number"
              value={formData.year}
              onChange={e => setFormData(prev => ({ ...prev, year: parseInt(e.target.value) }))}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={mutation.isPending}
              className="px-4 py-2 text-sm font-medium text-white bg-ocean-600 rounded-md hover:bg-ocean-700 disabled:opacity-50">
              {mutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
